// import node module libraries
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Col, Form, Row, Table} from 'react-bootstrap';
import {useFilters, useGlobalFilter, usePagination, useTable} from 'react-table';
import {FormSelect} from 'components/elements/form-select/FormSelect';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import ContratService from '../../services/ContratService';
import PersonnelService from '../../services/PersonnelService';

const ContratListItems = () => {
    const [originalContrats, setOriginalContrats] = useState([]);
    const [contrats, setContrats] = useState([]);
    const [personnel, setPersonnel] = useState([]);

    const TYPE_CHOICES = [
        {value: 'cdi', label: 'CDI'},
        {value: 'cdd', label: 'CDD'},
        {value: 'stage', label: 'Stage'}
    ];

    useEffect(() => {
        Promise.all([
            ContratService.getAllContrats(),
            PersonnelService.getAllActivePersonnels()
        ]).then(([contratsData, personnelData]) => {
            // Filter the contracts based on active personnel
            const activeContrats = contratsData.filter((contrat) =>
                personnelData.some((person) => person.id === contrat.personnel)
            );

            setOriginalContrats(activeContrats); // assuming you want to store the filtered contracts as the original ones
            setContrats(activeContrats);
            setPersonnel(personnelData);
        });
    }, []);

    const getFilterTerm = (event) => {
        let filterTerm = event.target.value;
        if (filterTerm !== '') {
            const newContratsList = originalContrats.filter((contrat) => {
                return contrat.type === filterTerm;
            });
            setContrats(newContratsList);
        } else {
            setContrats(originalContrats);
        }
    };

    const formatCategorieContrat = (value) => {
        return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getPersonnelById = (personnelId) => {
        const personnelObj =
            personnel.find((item) => item.id === personnelId) ?? {};

        return personnelObj ? personnelObj.nom + ' ' + personnelObj.prenom : '';
    };

    const columns = useMemo(
        () => [
            {accessor: 'id', Header: 'ID', show: false},
            {
                accessor: 'personnel',
                Header: 'Personnel',
                Cell: ({value}) => <h5 className="text-uppercase">{value}</h5>
            },

            {
                accessor: 'type',
                Header: 'Type',
                Cell: ({value}) => (
                    <div className="text-uppercase">{formatCategorieContrat(value)}</div>
                )
            },
            {
                accessor: 'salaire_brut',
                Header: 'Salaire Brut',
                Cell: ({value}) => <div>{Number(value).toLocaleString()} FCFA</div>
            },
            {
                accessor: 'date_debut',
                Header: 'Date début',
                Cell: ({value}) => <div>{value}</div>
            },
            {
                accessor: 'date_fin',
                Header: 'Date fin',
                Cell: ({value}) => <div>{value ? value : '--'}</div>
            }
        ],
        []
    );

    const data = useMemo(() => {
        return contrats.map((contrat) => ({
            ...contrat,
            personnel: getPersonnelById(contrat.personnel)
        }));
    }, [contrats]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        nextPage,
        previousPage,
        state,
        gotoPage,
        pageCount,
        prepareRow,
        setGlobalFilter
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageSize: 10,
                hiddenColumns: columns.map((column) => {
                    if (column.show === false) return column.accessor || column.id;
                    else return false;
                })
            }
        },
        useFilters,
        useGlobalFilter,
        usePagination
    );

    const {pageIndex, globalFilter} = state;

    return (
        <Fragment>
            <div className="overflow-hidden p-3">
                <Row className="justify-content-md-between mb-4 mb-xl-0 ">
                    <Col xl={2} lg={4} md={6} xs={12}>
                        <GlobalFilter
                            filter={globalFilter}
                            setFilter={setGlobalFilter}
                            placeholder="Rechercher contrat"
                        />
                    </Col>
                    <Col xxl={2} lg={2} md={6} xs={12}>
                        {/* records filtering options */}
                        <Form.Control
                            as={FormSelect}
                            placeholder="Filtrer par type"
                            options={TYPE_CHOICES}
                            onChange={getFilterTerm}
                        />
                    </Col>
                </Row>
            </div>

            <Table
                hover
                responsive
                {...getTableProps()}
                className="text-nowrap table-centered"
            >
                <thead className="table-light">
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => {
                                return (
                                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                );
                            })}
                        </tr>
                    );
                })}
                </tbody>
            </Table>

            <Pagination
                previousPage={previousPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                gotoPage={gotoPage}
                nextPage={nextPage}
            />
        </Fragment>
    );
};

export default ContratListItems;
